import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const WELCOME_MODAL_KEY = 'the-wall-welcome-seen';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const handleClose = useCallback(() => {
    // Save timestamp to localStorage
    const timestamp = new Date().toISOString();
    localStorage.setItem(WELCOME_MODAL_KEY, timestamp);
    onClose();
  }, [onClose]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md min-h-full"
        onClick={handleClose}
        aria-hidden
      />

      {/* Modal: scrollable on small viewports, max height so it doesn't overflow */}
      <div className="relative bg-wall-surface border border-wall-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] flex flex-col animate-scale-in overflow-hidden my-auto">
        {/* Header: minimal, confident */}
        <div className="shrink-0 px-4 pt-6 pb-5 sm:px-8 sm:pt-8 sm:pb-6 border-b border-wall-border/60">
          <h2 className="text-xl sm:text-2xl font-semibold text-wall-text tracking-tight">
            Welcome to The Wall
          </h2>
          <p className="mt-1.5 text-wall-muted text-sm sm:text-base">
            A few things to know before you start.
          </p>
        </div>

        {/* Content: scrollable when needed */}
        <div className="px-4 py-5 sm:px-8 sm:py-6 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-5 sm:space-y-6">
            {/* Painting */}
            <div className="flex gap-4">
              <div className="mt-1 shrink-0 flex flex-col items-center w-8">
                <span className="text-lg leading-none text-wall-muted/90" aria-hidden>
                  🎨
                </span>
                <div className="mt-2 w-px flex-1 min-h-6 bg-wall-border rounded-full" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-wall-text font-medium text-sm sm:text-base mb-1">
                  Open painting
                </h3>
                <p className="text-wall-muted text-xs sm:text-sm leading-relaxed">
                  Paint naturally without a daily ink limit or placement timer. Every pixel helps
                  push the wall toward its next expansion.
                </p>
              </div>
            </div>

            {/* Expansion */}
            <div className="flex gap-4">
              <div className="mt-1 shrink-0 flex flex-col items-center w-8">
                <span className="text-lg leading-none text-wall-muted/90" aria-hidden>
                  ✨
                </span>
                <div className="mt-2 w-px flex-1 min-h-6 bg-wall-border rounded-full" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-wall-text font-medium text-sm sm:text-base mb-1">
                  The canvas grows
                </h3>
                <p className="text-wall-muted text-xs sm:text-sm leading-relaxed">
                  When the Wall reaches{' '}
                  <span className="text-wall-text font-medium">85% filled</span>, it expands
                  permanently on every side.
                </p>
              </div>
            </div>

            {/* Cleanup & gallery */}
            <div className="flex gap-4">
              <div className="mt-1 shrink-0 flex flex-col items-center w-8">
                <span className="text-lg leading-none text-wall-muted/90" aria-hidden>
                  🖼️
                </span>
                <div className="mt-2 w-px flex-1 min-h-6 bg-wall-border rounded-full" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-wall-text font-medium text-sm sm:text-base mb-1">
                  Gallery record
                </h3>
                <p className="text-wall-muted text-xs sm:text-sm leading-relaxed">
                  The canvas is still cleaned up periodically, revealed when it happens. Finished
                  canvases stay in Gallery permanently, including the time-bar scroll.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-wall-border/60 bg-wall-bg/40 px-3 py-2.5">
              <p className="text-xs sm:text-sm text-wall-muted">
                <span className="font-medium text-wall-text">Quick tip:</span> use{' '}
                <span className="font-mono text-wall-text">I</span> or{' '}
                <span className="font-mono text-wall-text">1</span> for Inspect mode, and{' '}
                <span className="font-mono text-wall-text">P</span> or{' '}
                <span className="font-mono text-wall-text">2</span> for Paint mode.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0 px-4 py-4 sm:px-8 sm:py-5 border-t border-wall-border/60 bg-wall-bg/30">
          <button
            type="button"
            onClick={handleClose}
            className="w-full min-h-[48px] px-6 py-3 bg-wall-accent hover:bg-wall-accent-hover text-wall-bg font-medium rounded-xl transition-colors duration-200 active:scale-[0.99] touch-manipulation"
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Utility function to check if user has seen the welcome modal
export function hasSeenWelcomeModal(): boolean {
  return localStorage.getItem(WELCOME_MODAL_KEY) !== null;
}

// Utility function to get the timestamp when user closed the modal
export function getWelcomeModalTimestamp(): string | null {
  return localStorage.getItem(WELCOME_MODAL_KEY);
}
